import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	ClipboardCheck,
	Package,
	Search,
	RefreshCw,
	Play,
	Clock,
	CircleCheck as CheckCircle2,
	Calendar,
	Store,
	ArrowUpDown,
	ChevronDown,
	Filter,
	Loader as Loader2,
	TriangleAlert as AlertTriangle,
	Ban,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { getRelativeTime } from '../types/shopify'
import type { SortOption } from '../types/worker'
import { useReservationQueue, type ReservationQueueItem } from '../hooks/useReservationQueue'
import { useReservations } from '../hooks/useReservations'
import { ReservationPickModal } from '../components/reservation-terminal/ReservationPickModal'
import { getAdminId } from '../utils/getAdminId'

interface ReservationTerminalStats {
	toPick: number
	readiedToday: number
	fulfilledToday: number
}

export const ReservationTerminal: React.FC = () => {
	const { user } = useAuth()
	const [stats, setStats] = useState<ReservationTerminalStats>({ toPick: 0, readiedToday: 0, fulfilledToday: 0 })
	const [statsLoading, setStatsLoading] = useState(true)
	const [searchQuery, setSearchQuery] = useState('')
	const [sortBy, setSortBy] = useState<SortOption>('oldest')
	const [showSortMenu, setShowSortMenu] = useState(false)
	const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
	const [refreshing, setRefreshing] = useState(false)
	const [claimingId, setClaimingId] = useState<string | null>(null)
	const [claimError, setClaimError] = useState<string | null>(null)
	const [selected, setSelected] = useState<ReservationQueueItem | null>(null)
	const [modalOpen, setModalOpen] = useState(false)
	const [confirmExpireId, setConfirmExpireId] = useState<string | null>(null)
	const [expiringId, setExpiringId] = useState<string | null>(null)

	const adminId = useMemo(() => {
		if (!user) return null
		return getAdminId(user)
	}, [user])

	const {
		reservations,
		loading,
		error: queueError,
		totalCount,
		hasMore,
		loadReservations,
		loadMore,
		refresh,
		clearError,
	} = useReservationQueue({ adminId, pageSize: 25, sortBy })

	const { fulfillReservation, updateReservationStatus } = useReservations()

	const loadStats = useCallback(async () => {
		if (!adminId) return
		setStatsLoading(true)
		try {
			const { data, error } = await supabase.rpc('get_reservation_terminal_stats', { p_admin_id: adminId })
			if (error) throw error
			if (data && data.length > 0) {
				setStats({
					toPick: Number(data[0].to_pick) || 0,
					readiedToday: Number(data[0].readied_today) || 0,
					fulfilledToday: Number(data[0].fulfilled_today) || 0,
				})
			}
		} catch (err) {
			console.error('Failed to load reservation terminal stats:', err)
		} finally {
			setStatsLoading(false)
		}
	}, [adminId])

	useEffect(() => {
		loadStats()
		loadReservations()
	}, [loadStats, loadReservations])

	useEffect(() => {
		if (!loading) setLastUpdated(new Date())
	}, [loading, reservations])

	const handleRefresh = async () => {
		setRefreshing(true)
		clearError()
		await Promise.all([loadStats(), refresh()])
		setRefreshing(false)
	}

	const filtered = useMemo(() => {
		if (!searchQuery.trim()) return reservations
		const q = searchQuery.toLowerCase()
		return reservations.filter(
			(r) =>
				r.reservation_number.toLowerCase().includes(q) ||
				(r.customer_name && r.customer_name.toLowerCase().includes(q)) ||
				(r.source_order_number && r.source_order_number.toLowerCase().includes(q)) ||
				r.items.some(
					(it) =>
						(it.sku && it.sku.toLowerCase().includes(q)) ||
						(it.name && it.name.toLowerCase().includes(q))
				)
		)
	}, [reservations, searchQuery])

	const handleStartPick = async (reservation: ReservationQueueItem) => {
		if (!user || !adminId || claimingId) return
		setClaimingId(reservation.id)
		setClaimError(null)
		try {
			const { data, error } = await supabase.rpc('claim_reservation_for_picking', {
				p_reservation_id: reservation.id,
				p_admin_id: adminId,
				p_user_id: user.id,
			})
			if (error) throw error
			const res = data as { success: boolean; error?: string }
			if (!res.success) {
				setClaimError(res.error || 'Could not claim this reservation')
				await refresh()
				setTimeout(() => setClaimError(null), 4000)
				return
			}
			setSelected(reservation)
			setModalOpen(true)
		} catch {
			setClaimError('Failed to claim reservation. Please try again.')
			setTimeout(() => setClaimError(null), 4000)
		} finally {
			setClaimingId(null)
		}
	}

	const handleReleaseClaim = async (reservationId: string) => {
		if (!user || !adminId) return
		try {
			await supabase.rpc('release_reservation_claim', {
				p_reservation_id: reservationId,
				p_admin_id: adminId,
				p_user_id: user.id,
			})
		} catch {
			// best-effort release; the 30-min timeout reclaims it otherwise
		}
	}

	const handleComplete = async () => {
		setModalOpen(false)
		setSelected(null)
		await Promise.all([loadStats(), refresh()])
	}

	// Store-pickup reservations never auto-expire (they are paid orders), so an operator can
	// retire one manually here when the customer never collects it.
	const handleExpire = async (reservationId: string) => {
		if (expiringId) return
		setExpiringId(reservationId)
		try {
			await updateReservationStatus(reservationId, 'expired')
			setConfirmExpireId(null)
			await Promise.all([loadStats(), refresh()])
		} finally {
			setExpiringId(null)
		}
	}

	const displayedCount = filtered.length

	return (
		<div className="min-h-screen bg-gray-50 dark:bg-gray-900">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
				<div className="mb-8">
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
						<div className="flex items-center gap-4">
							<div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
								<ClipboardCheck className="h-7 w-7 text-white" />
							</div>
							<div>
								<h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reservation Terminal</h1>
								<p className="text-sm text-gray-500 dark:text-gray-400">
									Pick and fulfil customer reservations
								</p>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<span className="text-xs text-gray-500 dark:text-gray-400">
								Updated {getRelativeTime(lastUpdated.toISOString())}
							</span>
							<Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
								<RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
								Refresh
							</Button>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
					<div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
								<Package className="h-6 w-6 text-amber-600 dark:text-amber-400" />
							</div>
							{statsLoading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : null}
						</div>
						<p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Reservations to Pick</p>
						<p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{statsLoading ? '-' : stats.toPick}
						</p>
					</div>

					<div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
								<Calendar className="h-6 w-6 text-blue-600 dark:text-blue-400" />
							</div>
						</div>
						<p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Readied Today</p>
						<p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
							{statsLoading ? '-' : stats.readiedToday}
						</p>
					</div>

					<div className="bg-gradient-to-br from-cyan-500 to-teal-600 rounded-2xl p-6 shadow-lg">
						<div className="flex items-center justify-between mb-4">
							<div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
								<CheckCircle2 className="h-6 w-6 text-white" />
							</div>
						</div>
						<p className="text-sm font-medium text-white/80 mb-1">Fulfilled Today</p>
						<p className="text-3xl font-bold text-white">{statsLoading ? '-' : stats.fulfilledToday}</p>
					</div>
				</div>

				{claimError && (
					<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3">
						<AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
						<p className="text-sm text-red-700 dark:text-red-300">{claimError}</p>
					</div>
				)}

				{queueError && (
					<div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-center justify-between">
						<div className="flex items-center gap-3">
							<AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
							<p className="text-sm text-amber-700 dark:text-amber-300">{queueError.message}</p>
						</div>
						{queueError.retryable && (
							<Button variant="secondary" size="sm" onClick={handleRefresh} disabled={refreshing}>
								{refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Retry'}
							</Button>
						)}
					</div>
				)}

				<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
					<div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-700">
						<div className="flex flex-col sm:flex-row gap-4">
							<div className="flex-1 relative">
								<Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
								<input
									type="text"
									placeholder="Search by reservation #, customer, SKU..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
									className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
								/>
							</div>
							<div className="flex gap-3">
								<div className="relative">
									<button
										onClick={() => setShowSortMenu(!showSortMenu)}
										className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
									>
										<ArrowUpDown className="h-4 w-4" />
										<span className="hidden sm:inline">
											{sortBy === 'oldest' ? 'Oldest First' : 'Newest First'}
										</span>
										<ChevronDown className="h-4 w-4" />
									</button>
									{showSortMenu && (
										<>
											<div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
											<div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-20 overflow-hidden">
												<button
													onClick={() => {
														setSortBy('oldest')
														setShowSortMenu(false)
													}}
													className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
														sortBy === 'oldest'
															? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
															: 'text-gray-700 dark:text-gray-300'
													}`}
												>
													Oldest First (Recommended)
												</button>
												<button
													onClick={() => {
														setSortBy('newest')
														setShowSortMenu(false)
													}}
													className={`w-full px-4 py-3 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 ${
														sortBy === 'newest'
															? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
															: 'text-gray-700 dark:text-gray-300'
													}`}
												>
													Newest First
												</button>
											</div>
										</>
									)}
								</div>
								<div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600">
									<Filter className="h-4 w-4 text-gray-400" />
									<span className="text-sm font-medium text-gray-700 dark:text-gray-300">
										{searchQuery ? `${displayedCount} found` : `${displayedCount} of ${totalCount}`}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div className="divide-y divide-gray-100 dark:divide-gray-700">
						{loading ? (
							<div className="p-12 text-center">
								<Loader2 className="h-10 w-10 animate-spin text-cyan-500 mx-auto mb-4" />
								<p className="text-gray-500 dark:text-gray-400">Loading reservations...</p>
							</div>
						) : filtered.length === 0 ? (
							<div className="p-12 text-center">
								<div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
									<Package className="h-8 w-8 text-gray-400" />
								</div>
								<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
									{searchQuery ? 'No matching reservations' : 'All caught up!'}
								</h3>
								<p className="text-gray-500 dark:text-gray-400">
									{searchQuery
										? 'Try adjusting your search terms'
										: 'There are no reservations waiting to be picked.'}
								</p>
							</div>
						) : (
							filtered.map((reservation) => (
								<div
									key={reservation.id}
									className="p-4 sm:p-6 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
								>
									<div className="flex flex-col lg:flex-row lg:items-center gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-3 mb-2">
												<h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
													{reservation.reservation_number}
												</h3>
												<span
													className={`px-2.5 py-1 text-xs font-medium rounded-full ${
														reservation.status === 'ready'
															? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
															: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
													}`}
												>
													{reservation.status === 'ready' ? 'Ready' : 'Reserved'}
												</span>
												{reservation.is_store_pickup && (
													<span className="px-2.5 py-1 text-xs font-medium rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 flex items-center gap-1">
														<Store className="h-3 w-3" /> Pick up from store
													</span>
												)}
											</div>

											<div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
												<span className="flex items-center gap-1.5">
													<Clock className="h-4 w-4" />
													{getRelativeTime(reservation.created_at)}
												</span>
												<span>{reservation.customer_name || 'Customer'}</span>
												{reservation.customer_phone && <span>{reservation.customer_phone}</span>}
												{reservation.source_order_number && (
													<span>Order {reservation.source_order_number}</span>
												)}
												<span className="flex items-center gap-1.5">
													<Package className="h-4 w-4" />
													{reservation.unit_count} units across {reservation.item_count} items
												</span>
											</div>

											{reservation.items.length > 0 && (
												<div className="mt-3 flex flex-wrap gap-2">
													{reservation.items.slice(0, 3).map((item) => (
														<span
															key={item.item_id}
															className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-lg"
														>
															{item.sku || (item.name || '').substring(0, 20)}
															{item.quantity > 1 && ` x${item.quantity}`}
														</span>
													))}
													{reservation.items.length > 3 && (
														<span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-lg">
															+{reservation.items.length - 3} more
														</span>
													)}
												</div>
											)}
										</div>

										<div className="flex items-center gap-3">
											{confirmExpireId === reservation.id ? (
												<div className="flex items-center gap-2">
													<span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:inline">
														Expire this pickup?
													</span>
													<Button
														variant="secondary"
														size="sm"
														onClick={() => handleExpire(reservation.id)}
														disabled={expiringId === reservation.id}
														className="text-red-600 dark:text-red-400"
													>
														{expiringId === reservation.id ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															'Confirm'
														)}
													</Button>
													<Button
														variant="secondary"
														size="sm"
														onClick={() => setConfirmExpireId(null)}
														disabled={expiringId === reservation.id}
													>
														Cancel
													</Button>
												</div>
											) : (
												reservation.is_store_pickup && (
													<Button
														variant="secondary"
														size="sm"
														onClick={() => setConfirmExpireId(reservation.id)}
														title="Expire this store-pickup reservation"
													>
														<Ban className="h-4 w-4 sm:mr-2" />
														<span className="hidden sm:inline">Expire</span>
													</Button>
												)
											)}
											<Button
												onClick={() => handleStartPick(reservation)}
												disabled={claimingId === reservation.id}
												className="flex-shrink-0 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 disabled:opacity-60"
											>
												{claimingId === reservation.id ? (
													<Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
												) : (
													<Play className="h-4 w-4 sm:mr-2" />
												)}
												<span className="hidden sm:inline">
													{claimingId === reservation.id ? 'Claiming...' : 'Pick Reservation'}
												</span>
											</Button>
										</div>
									</div>
								</div>
							))
						)}
					</div>

					{!loading && filtered.length > 0 && !searchQuery && (
						<div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
							<p className="text-sm text-gray-500 dark:text-gray-400">
								Showing {displayedCount} of {totalCount} reservations
							</p>
							{hasMore && (
								<Button variant="secondary" onClick={loadMore} disabled={loading}>
									{loading ? (
										<>
											<Loader2 className="h-4 w-4 mr-2 animate-spin" />
											Loading...
										</>
									) : (
										'Load More'
									)}
								</Button>
							)}
						</div>
					)}
				</div>
			</div>

			{modalOpen && selected && (
				<ReservationPickModal
					reservation={selected}
					adminId={adminId}
					fulfillReservation={fulfillReservation}
					updateReservationStatus={updateReservationStatus}
					onClose={() => {
						handleReleaseClaim(selected.id)
						setModalOpen(false)
						setSelected(null)
						refresh()
					}}
					onComplete={handleComplete}
				/>
			)}
		</div>
	)
}

export default ReservationTerminal
